import { useEffect, useMemo, useState } from 'react';
import { MAX_REVIEW_ITEMS } from '../../services/referenceValuesService';
import { formatValue, rowId } from './helpers';

const SourceLink = ({ source }) => (source?.source_url ? (
  <a href={source.source_url} target="_blank" rel="noopener noreferrer">{source.source_ref}</a>
) : (source?.source_ref || '—'));

/**
 * Proposals from the refresh, waiting for a reviewer. Nothing here reaches a
 * prompt until it is approved; the current value (if any) stays in use.
 */
const PendingPanel = ({ rows, onApprove, onReject, busy }) => {
  const [selected, setSelected] = useState(() => new Set());

  // Drop selections for rows that are no longer pending.
  useEffect(() => {
    const ids = new Set(rows.map(rowId));
    setSelected((prev) => new Set([...prev].filter((id) => ids.has(id))));
  }, [rows]);

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const chosen = useMemo(() => rows.filter((row) => selected.has(rowId(row))), [rows, selected]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map(rowId)));

  if (!rows.length) {
    return (
      <div className="alert alert-success" role="status">
        <i className="bi bi-check-circle me-2"></i>
        Nothing waiting for review.
      </div>
    );
  }

  return (
    <div className="card mb-4" data-testid="pending-panel">
      <div className="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <span>
          <i className="bi bi-hourglass-split me-2"></i>
          <strong>{rows.length}</strong> {rows.length === 1 ? 'value' : 'values'} waiting for review
        </span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-success btn-sm"
            disabled={busy || !chosen.length || chosen.length > MAX_REVIEW_ITEMS}
            onClick={() => onApprove(chosen)}
          >
            <i className="bi bi-check-circle me-1"></i>
            Approve selected ({chosen.length})
          </button>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            disabled={busy || !chosen.length || chosen.length > MAX_REVIEW_ITEMS}
            onClick={() => onReject(chosen)}
          >
            <i className="bi bi-x-circle me-1"></i>
            Reject selected
          </button>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-hover mb-0 align-middle">
          <thead>
            <tr>
              <th style={{ width: '2rem' }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  aria-label="Select all"
                  checked={allSelected}
                  onChange={toggleAll}
                />
              </th>
              <th>Year</th>
              <th>Value</th>
              <th className="text-end">Current</th>
              <th className="text-end">Proposed</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const id = rowId(row);
              const conflict = row.proposed.conflict || [];
              return (
                <tr key={id}>
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      aria-label={`Select ${row.year} ${row.label}`}
                      checked={selected.has(id)}
                      onChange={() => toggle(id)}
                    />
                  </td>
                  <td>{row.year}</td>
                  <td>{row.label}</td>
                  <td className="text-end text-muted">{formatValue(row.value, row.unit)}</td>
                  <td className="text-end fw-semibold">{formatValue(row.proposed.value, row.unit)}</td>
                  <td>
                    <SourceLink source={row.proposed} />
                    {conflict.map((other) => (
                      <div key={other.source_ref} className="small text-danger">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        <SourceLink source={other} /> says {formatValue(other.value, row.unit)}
                      </div>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PendingPanel;
